import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Search, Box, Tag, User, Loader2 } from "lucide-react";

const Section = ({ title, children }) => (
  <div className="py-1">
    <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest text-muted-foreground">{title}</p>
    {children}
  </div>
);

const Item = ({ icon, label, tag, onClick, testid }) => (
  <button
    data-testid={testid}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-secondary text-left transition-colors"
  >
    {icon}
    <span className="flex-1 truncate">{label}</span>
    {tag && <span className="font-mono text-[10px] text-muted-foreground uppercase">{tag}</span>}
  </button>
);

export default function SearchAutocomplete({ className = "" }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setRes(null);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/search/suggest", { params: { q: term } });
        setRes(data);
        setOpen(true);
      } catch {
        setRes(null);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const go = (path) => {
    setOpen(false);
    setQ("");
    nav(path);
  };
  const submit = (e) => {
    e.preventDefault();
    const term = q.trim();
    if (term) go(`/marketplace?q=${encodeURIComponent(term)}`);
  };

  const hasResults = res && (res.listings?.length || res.categories?.length || res.designers?.length);

  return (
    <div ref={boxRef} className={`relative ${className}`}>
      <form onSubmit={submit} className="relative w-full">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          data-testid="nav-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => res && setOpen(true)}
          placeholder="Search CAD files, categories, designers…"
          autoComplete="off"
          className="w-full bg-secondary/60 border border-border rounded-md pl-9 pr-9 py-2 text-sm focus:outline-none focus:border-primary"
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
      </form>

      {open && q.trim().length >= 2 && (
        <div data-testid="search-suggestions" className="absolute z-50 mt-1 w-full bg-card border border-border rounded-md shadow-lg overflow-hidden max-h-[70vh] overflow-y-auto">
          {!hasResults && !loading && (
            <div className="px-3 py-4 text-sm text-muted-foreground">No matches for "{q.trim()}"</div>
          )}
          {res?.listings?.length > 0 && (
            <Section title="Designs">
              {res.listings.map((l) => (
                <Item key={l.id} testid={`suggest-listing-${l.id}`} icon={<Box className="h-3.5 w-3.5 text-primary" />} onClick={() => go(`/design/${l.id}`)} label={l.title} tag={l.file_type} />
              ))}
            </Section>
          )}
          {res?.categories?.length > 0 && (
            <Section title="Categories">
              {res.categories.map((c) => (
                <Item key={c.id} testid={`suggest-cat-${c.id}`} icon={<Tag className="h-3.5 w-3.5 text-muted-foreground" />} onClick={() => go(`/marketplace?category=${c.id}`)} label={c.name} />
              ))}
            </Section>
          )}
          {res?.designers?.length > 0 && (
            <Section title="Designers">
              {res.designers.map((d) => (
                <Item key={d.id} testid={`suggest-designer-${d.id}`} icon={<User className="h-3.5 w-3.5 text-muted-foreground" />} onClick={() => go(`/designer/${d.id}`)} label={d.name} />
              ))}
            </Section>
          )}
          {hasResults ? (
            <button
              data-testid="suggest-see-all"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => go(`/marketplace?q=${encodeURIComponent(q.trim())}`)}
              className="w-full text-left px-3 py-2.5 text-sm font-medium text-primary border-t border-border hover:bg-secondary transition-colors"
            >
              See all results for "{q.trim()}" →
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}
